import * as React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AuthProvider, useAuth } from './auth/AuthContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { ROLES, homePathFor } from './auth/roles';

import MainLayout from './components/MainLayout';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import Forbidden from './pages/Forbidden';

// Lazy-load page bundles so the initial Login route stays cheap.
const RequestsList = React.lazy(() => import('./pages/insurance/RequestsList'));
const NewRequest = React.lazy(() => import('./pages/insurance/NewRequest'));
const RequestDetail = React.lazy(() => import('./pages/insurance/RequestDetail'));
const SupplierOpenRequests = React.lazy(
	() => import('./pages/supplier/OpenRequests')
);
const SupplierMyQuotations = React.lazy(
	() => import('./pages/supplier/MyQuotations')
);
const SupplierMyQuotationDetail = React.lazy(
	() => import('./pages/supplier/MyQuotationDetail')
);
const PublicQuoteForm = React.lazy(() => import('./pages/supplier/PublicQuote'));
const QuoteSuccess = React.lazy(() => import('./pages/supplier/QuoteSuccess'));
const MechanicReports = React.lazy(() => import('./pages/mechanic/Reports'));
const NewCarReport = React.lazy(() => import('./pages/mechanic/NewReport'));
const ReportDetail = React.lazy(() => import('./pages/mechanic/ReportDetail'));
const Team = React.lazy(() => import('./pages/admin/Team'));
const Settings = React.lazy(() => import('./pages/Settings'));

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30_000, // 30s — pages re-fetch on focus, not aggressively
			retry: (count, err: any) => {
				// Don't retry auth errors — the interceptor already handled them
				if (err?.response?.status === 401 || err?.response?.status === 403) return false;
				return count < 1;
			},
			refetchOnWindowFocus: false,
		},
	},
});

function PageFallback() {
	return (
		<div className="p-8">
			<div className="animate-pulse space-y-3 max-w-3xl">
				<div className="h-6 w-1/3 bg-slate-200 rounded" />
				<div className="h-4 w-2/3 bg-slate-200 rounded" />
				<div className="h-4 w-1/2 bg-slate-200 rounded" />
			</div>
		</div>
	);
}

/** Land the user on the right home for their role. */
function RoleHomeRedirect() {
	const { user, status } = useAuth();
	if (status === 'loading') return <PageFallback />;
	return <Navigate to={user ? homePathFor(user.role) : '/login'} replace />;
}

export default function App() {
	return (
		<BrowserRouter>
			<QueryClientProvider client={queryClient}>
				<AuthProvider>
					<React.Suspense fallback={<PageFallback />}>
						<Routes>
							{/* Public */}
							<Route path="/login" element={<Login />} />
							<Route path="/forbidden" element={<Forbidden />} />

							{/* Public supplier landing — supplier opens this from a WhatsApp link.
							 *  We deliberately keep this OUTSIDE the protected shell. */}
							<Route path="/quote/:reference" element={<PublicQuoteForm />} />
							<Route path="/quote/:reference/success" element={<QuoteSuccess />} />

							{/* Authenticated app */}
							<Route
								element={
									<ProtectedRoute>
										<MainLayout />
									</ProtectedRoute>
								}
							>
								<Route path="/" element={<RoleHomeRedirect />} />

								{/*
								 * /requests is shared by the insurance flow and the mechanic-
								 * direct flow. Tenant scoping on the BE returns only the
								 * caller's own Requests, so the same list/detail components
								 * render correctly for either role. /requests/new stays
								 * insurance-only — mechanics create direct Requests via the
								 * Supplier tab on /mechanic/new.
								 */}
								<Route
									path="/requests"
									element={
										<ProtectedRoute
											roles={[
												ROLES.INSURANCE_ADMIN,
												ROLES.INSURANCE_STAFF,
												ROLES.SUPER_ADMIN,
												ROLES.MECHANIC,
											] as any}
										>
											<RequestsList />
										</ProtectedRoute>
									}
								/>
								<Route
									path="/requests/new"
									element={
										<ProtectedRoute
											roles={[
												ROLES.INSURANCE_ADMIN,
												ROLES.INSURANCE_STAFF,
												ROLES.SUPER_ADMIN,
											] as any}
										>
											<NewRequest />
										</ProtectedRoute>
									}
								/>
								<Route
									path="/requests/:requestId"
									element={
										<ProtectedRoute
											roles={[
												ROLES.INSURANCE_ADMIN,
												ROLES.INSURANCE_STAFF,
												ROLES.SUPER_ADMIN,
												ROLES.MECHANIC,
											] as any}
										>
											<RequestDetail />
										</ProtectedRoute>
									}
								/>

								{/* Supplier */}
								<Route
									path="/supplier"
									element={
										<ProtectedRoute
											roles={[ROLES.SUPPLIER_ADMIN, ROLES.SUPPLIER_STAFF] as any}
										>
											<SupplierOpenRequests />
										</ProtectedRoute>
									}
								/>
								<Route
									path="/supplier/quotations"
									element={
										<ProtectedRoute
											roles={[ROLES.SUPPLIER_ADMIN, ROLES.SUPPLIER_STAFF] as any}
										>
											<SupplierMyQuotations />
										</ProtectedRoute>
									}
								/>
								<Route
									path="/supplier/quotations/:reference"
									element={
										<ProtectedRoute
											roles={[ROLES.SUPPLIER_ADMIN, ROLES.SUPPLIER_STAFF] as any}
										>
											<SupplierMyQuotationDetail />
										</ProtectedRoute>
									}
								/>

								{/* Mechanic */}
								<Route
									path="/mechanic"
									element={
										<ProtectedRoute roles={[ROLES.MECHANIC] as any}>
											<MechanicReports />
										</ProtectedRoute>
									}
								/>
								<Route
									path="/mechanic/new"
									element={
										<ProtectedRoute roles={[ROLES.MECHANIC] as any}>
											<NewCarReport />
										</ProtectedRoute>
									}
								/>
								<Route
									path="/mechanic/reports/:reportId"
									element={
										<ProtectedRoute roles={[ROLES.MECHANIC] as any}>
											<ReportDetail />
										</ProtectedRoute>
									}
								/>
								<Route
									path="/mechanic/reports/:reportId/edit"
									element={
										<ProtectedRoute roles={[ROLES.MECHANIC] as any}>
											<NewCarReport />
										</ProtectedRoute>
									}
								/>

								{/* Admin */}
								<Route path="/team" element={<Team />} />
								<Route path="/settings" element={<Settings />} />

								{/* Insurance can also see car-reports (alias). Same components,
							    different role gate — ReportDetail uses the auth context to
							    render the right action bar (convert/reject vs. edit/delete). */}
								<Route
									path="/car-reports"
									element={
										<ProtectedRoute
											roles={[
												ROLES.INSURANCE_ADMIN,
												ROLES.INSURANCE_STAFF,
												ROLES.SUPER_ADMIN,
											] as any}
										>
											<MechanicReports />
										</ProtectedRoute>
									}
								/>
								<Route
									path="/car-reports/:reportId"
									element={
										<ProtectedRoute
											roles={[
												ROLES.INSURANCE_ADMIN,
												ROLES.INSURANCE_STAFF,
												ROLES.SUPER_ADMIN,
											] as any}
										>
											<ReportDetail />
										</ProtectedRoute>
									}
								/>
							</Route>

							<Route path="*" element={<NotFound />} />
						</Routes>
					</React.Suspense>
				</AuthProvider>
			</QueryClientProvider>
		</BrowserRouter>
	);
}
